import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { callApi } from "../../../../../services/MonitoringPlanService";
import { API_ROUTES } from "../../ApiRoutes/ApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";

const C21GreenhouseEmissions = forwardRef(({ c21GhgEmissions, vesselMstId, dropdownData, refetchMonitoringData, shouldRefetch }, ref) => {
  const methods = useForm({});
  const { reset } = methods;
  const [activeAction, setactiveAction] = useState(-1);
  const [validationErrors, setValidationErrors] = useState({});
  const [fuelData, setFuelData] = useState([]);
  const [hasFetched, setHasFetched] = useState(false);
  const RowActions = {
    Edit: 0,
    Add: 1,
  };

  useEffect(() => {
    if (shouldRefetch && !hasFetched) {
      fetchEmissionSources();
      setHasFetched(true);
    }
  }, [shouldRefetch]);

  useEffect(() => {
    if (!shouldRefetch) {
      setHasFetched(false);
    }
  }, [shouldRefetch]);

  const getEmissionsSource = () => {
    return (
      dropdownData?.monitoringMethodOptions.map((type) => ({
        label: type.name,
        value: type.code,
      })) || []
    );
  };

  useEffect(() => {
    if (Array.isArray(c21GhgEmissions)) {
      const transformedData = c21GhgEmissions?.map((item) => {
        return {
          emissionSourceRefNo: item?.emissionSourceRefNo || '',
          emissionSourceName: item?.emissionSourceName || '',
          emissionSourceType: item?.emissionSourceType || '',
          emissionGuid: item?.emissionGuid || "",
          equipmentName: item?.equipmentName || "",
          chosenMethods: Array.isArray(item.chosenMethods)
            ? item.chosenMethods
            : typeof item.chosenMethods === 'string'
              ? item.chosenMethods.split(',')?.map((v) => v.trim())
              : [],
        };
      });

      setFuelData(transformedData);
    }
  }, [c21GhgEmissions]);
  const emissionSourceTypeOptions = useMemo(getEmissionsSource, [dropdownData]);

  useEffect(() => {
    if (c21GhgEmissions) {
      reset({
        emissionSourceRefNo: c21GhgEmissions.emissionSourceRefNo || "",
        emissionSourceName: c21GhgEmissions.emissionSourceName || "",
        emissionSourceType: c21GhgEmissions.emissionSourceType || "",
        chosenMethods: c21GhgEmissions.chosenMethods || [],
        equipmentName: c21GhgEmissions.equipmentName || ""
      });
    }
  }, [c21GhgEmissions, reset]);

  const handleFuelActions = async (table, row, action) => {
    if (activeAction === -1) {
      if (action === RowActions.Edit) {
        table.setEditingRow(row);
        setactiveAction(action);
      } else if (action === RowActions.Add) {
        table.setCreatingRow(true);
        setactiveAction(action);
      }
      return;
    } else return;
  };

  const resetActiveRow = () => {
    setactiveAction(-1);
    setValidationErrors({});
  };

  const validateRequired = (value) => {
    if (typeof value === "string") {
      return !!value.length;
    } else if (typeof value === "number") {
      return value >= 0;
    }
    return false;
  };

  const validateUser = (row) => {
    return {
      chosenMethods: !row.chosenMethods || row.chosenMethods.length === 0
        ? "Field is required"
        : "",
      equipmentName: !validateRequired(row.equipmentName)
        ? "Field is required"
        : "",
    };
  };

  useImperativeHandle(ref, () => ({
    async submitAndCheck() {
      const { creatingRow, editingRow } = table.getState();
      if (!creatingRow && !editingRow) return true;
      const mode = creatingRow ? "add" : "edit";
      const editedValues = creatingRow ? creatingRow?._valuesCache : editingRow?._valuesCache;
      let isDirty = false;
      if (editingRow && editedValues) {
        isDirty = Object.keys(editedValues).some(
          (key) => editedValues[key] !== editingRow.original[key]
        );
      }
      const validationRes = validateUser(editedValues, mode, {});
      const hasErrors = Object.values(validationRes).some((err) => err);

      if (hasErrors) {
        setValidationErrors(validationRes);
        emitToasterMsg(
          "warning",
          "Validation Error",
          "Please correct the highlighted fields before proceeding."
        );
        return false;
      }

      // Autosave on valid & dirty
      if (editingRow && isDirty) {
        await handleUpdateEmissions({ values: editedValues, table, row: editingRow }, true);
        return true;
      }
      if (!isDirty) {
        setactiveAction(-1)
        table.setEditingRow(null);
        return true;
      }
      return true;
    }
  }));

  const handleUpdateEmissions = async ({ values, table, row }, isAuto = false) => {
    const newValidationErrors = validateUser(values, "add", {});
    if (Object.values(newValidationErrors).some((error) => error)) {
      setValidationErrors(newValidationErrors);
      return;
    }
    setValidationErrors({});

    const updatedData = fuelData.map((item, index) => {
      if (index === row.index) {
        return {
          ...item,
          emissionSourceRefNo: values?.emissionSourceRefNo,
          emissionSourceName: values?.emissionSourceName,
          emissionSourceType: values?.emissionSourceType,
          emissionGuid: item?.emissionGuid || "",
          chosenMethods: values?.chosenMethods || [],
          equipmentName: values?.equipmentName || ""
        };
      }
      return item;
    });

    const finalPayload = updatedData?.map((item) => ({
      ...item,
      C21GreenhouseEmissionsId: item.C21GreenhouseEmissionsId || 0
    }));

    try {
      const C21GreenhouseEmissions = await callApi({
        method: "POST",
        url: API_ROUTES.C21(vesselMstId),
        data: finalPayload,
      });
      if (C21GreenhouseEmissions?.isSuccess) {
        emitToasterMsg("success", "Table C.2.1", isAuto ? "GHG gas emissions has been autosaved successfully" : "GHG gas emissions has been updated successfully");
        table.setEditingRow(null);
        setactiveAction(-1);
        setFuelData(finalPayload);
        if (typeof refetchMonitoringData === "function") {
          await refetchMonitoringData();
        }
      } else {
        emitToasterMsg("error", "Table C.2.1", C21GreenhouseEmissions?.errorMessage);
      }
    } catch (error) {
      emitToasterMsg("error", "Table C.2.1", error?.errorMessage);
    }
  };
  const fetchEmissionSources = async () => {
    try {
      const response = await callApi({
        method: "GET",
        url: API_ROUTES.GET_B3_PREFILL_TO_C21(vesselMstId),
        data: {}
      });
      if (Array.isArray(response)) {
        const formattedData = response.map((item) => ({
          emissionSourceRefNo: item.emissionSourceRefNo,
          emissionSourceName: item.emissionSourceName,
          emissionSourceType: item.emissionSourceType,
          chosenMethods: item.chosenMethods || [],
          equipmentName: item.equipmentName,
          emissionGuid: item?.emissionGuid || "",
        }));
        setFuelData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching emission sources:", error);
    }
  };

  useEffect(() => {
    fetchEmissionSources();
  }, [vesselMstId]);

  useEffect(() => {
    if (c21GhgEmissions) {
      reset(prev => ({ ...prev, c21: c21GhgEmissions }));
    }
  }, [c21GhgEmissions, reset]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "emissionSourceRefNo",
        header: "Emissions source reference No",
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
      },
      {
        accessorKey: "emissionSourceName",
        header: "Name of the emissions source",
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
      },
      {
        accessorKey: "emissionSourceType",
        header: "Emissions source type",
        enableEditing: false,
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
        Cell: ({ cell }) => {
          const code = cell.getValue();
          const name =
            dropdownData?.emissionSourceTypes?.find((opt) => opt.code === code)?.name ||
            code || "";
          return name;
        },
      },
      {
        accessorKey: "chosenMethods",
        header: "Chosen method(s)",
        editVariant: 'select',
        size: 330,
        editSelectOptions: emissionSourceTypeOptions,
        muiEditTextFieldProps: ({ cell, row }) => ({
          select: true,
          required: true,
          value: cell.getValue() || [],
          error: !!validationErrors?.chosenMethods,
          helperText: validationErrors?.chosenMethods,
          SelectProps: {
            multiple: true,
            renderValue: (selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((code) => {
                  const label = dropdownData.monitoringMethodOptions?.find(opt => opt.code === code)?.name || code;
                  return (
                    <Chip
                      key={code}
                      label={label}
                      onDelete={(e) => {
                        e.stopPropagation();
                        const newValue = selected.filter((v) => v !== code);
                        row._valuesCache["chosenMethods"] = newValue;
                        setValidationErrors((prev) => ({
                          ...prev,
                          chosenMethods: undefined,
                        }));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  );
                })}
              </Box>
            ),
            MenuProps: {
              disableScrollLock: true,
              PaperProps: {
                sx: { maxHeight: 250 },
              },
            },
          },
          onChange: (event) => {
            const value = event.target.value;
            row._valuesCache["chosenMethods"] = value;
            setValidationErrors((prev) => ({
              ...prev,
              chosenMethods: undefined,
            }));
          },
        }),
        Cell: ({ cell }) => {
          const selectedCodes = cell.getValue() || [];
          return selectedCodes.map(code =>
            dropdownData?.monitoringMethodOptions.find(opt => opt.code === code)?.name || code
          ).join(', ');
        },
      },
      {
        accessorKey: "equipmentName",
        header: "Measurement equipment (name)",
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
            verticalAlign: "middle"
          },
        },
        muiEditTextFieldProps: ({ row }) => ({
          placeholder: "Measurement equipment",
          required: true,
          error: !!validationErrors?.equipmentName,
          helperText: validationErrors?.equipmentName,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              equipmentName: undefined,
            }),
          inputProps: {
            maxLength: 255,
          },
        }),
      },
    ]
  )

  const table = useMaterialReactTable({
    columns,
    data: fuelData || [],
    enableStickyHeader: true,
    enableSorting: false,
    enableRowSelection: false,
    enableColumnPinning: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    createDisplayMode: "row",
    editDisplayMode: "row",
    enableEditing: true,
    enablePagination: false,
    muiTableHeadCellProps: {
      sx: {
        color: "rgb(255 255 255)",
        backgroundColor: "#042a4c",
        position: "sticky",
        borderRight: "1px solid lightgray",
        "&:last-child": {
          borderRight: "none",
        },
      },
    },
    muiTablePaperProps: {
      sx: {
        boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: "520px",
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        muiTableHeadCellProps: {
          sx: {
            borderBottom: 'none',
            borderRight: '1px solid lightgray',
          },
        },
      },
    },
    muiTableBodyCellProps: ({ row, cell }) => {
      return {
        sx: {
          borderRight: "1px solid lightgray",
          "&:last-child": {
            borderRight: "none",
          },
          color: "",
        },
      };
    },
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        "&:not(:last-of-type)": {
          borderBottom: "1px solid lightgray",
        },
        verticalAlign: "top",
      },
    }),
    muiTableHeadRowProps: {
      sx: {
        borderBottom: "2px solid lightgray",
      },
    },
    columnOrder: [
      "emissionSource",
      "nameOfEmissionsSource",
      "typeOfEmissionsSource",
      "emissionsSourceClass",
      "techinal",
      "nameOfEmissionsSource"
    ],
    getRowId: (row) => { },
    onEditingRowCancel: resetActiveRow,
    onCreatingRowCancel: resetActiveRow,
    onEditingRowSave: handleUpdateEmissions,
    renderToolbarInternalActions: ({ table }) => <></>,
    renderRowActions: ({ row, table }) => {
      return (
        <Box sx={{ display: "flex", gap: "1rem" }}>
          <Tooltip title="Edit">
            <IconButton
              style={{ padding: "0px" }}
              onClick={() =>
                handleFuelActions(
                  table,
                  row,
                  RowActions.Edit
                )}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </Box>
      );
    },
  });

  return (
    <div className="c21-greenhouseemissions mt-1">
      <MaterialReactTable table={table} />
    </div>
  );
})

export default C21GreenhouseEmissions;